import { makeAutoObservable, runInAction } from 'mobx';
import { ACCEPT_FILE_TYPE, FILE_TYPE, getFileType, IMG_FILE_ICONS } from '../input-file/shared';
import { getImageMessageUrl, minifyAliImage } from './file';
import { HelperCore, HelpOther } from '@/common/helper';
import userStore from '@/store/user';
import chatStoreManager from '@/store/chat/manager';
import client from '@/store/client';
import globalStore from '@/store/global';
import { cloneDeep, uniqBy } from 'lodash-es';
import mutilKnowledgeStore from '../chat/MultiKnowledge/store';
import kbStore from '@/components/kb-node-tree/kb-store';
import { isEnableEnterpriseKbPage } from '@/components/kb-node-tree/utils'

class Store {
  // chat 入参
  sendParams = {
    /**
     * 消息类型 role_biz
     *
     * translation:翻译
     * doc:文档
     * chat_image:图片对话
     * extract_image:图片提取文字
     * explain_image:图片解释
     * page:网页对话
     */
    // role_biz : "",
  };

  /**
   * @type { Array<File> | null }
   */
  tempFile = null; // 选择的文件 file 0526 已改为普通数组

  /**
   * @type { Array<File> | null }
   */
  tempUploaded = null; // 上传文件结果

  // 用于激活输入框
  inputId = null;

  get sendDisabled() {
    if (this.tempFile && !this.tempUploaded) {
      return this.isKnowledgeFile ? false : true
    }
    return false;
  }

  get supportUploadFile () {
    const support_all_chat_doc = globalStore.cloudConfig.webConfig?.support_all_chat_doc;
    if (support_all_chat_doc == 1) {
      return true
    }
    return (
      support_all_chat_doc == 2
      && !client.isLLQ
    ) || (
      support_all_chat_doc == 3
      && client.isLLQ
    )
  }

  get isKnowledgeFile () {
    if(!this.tempFile){
      return false;
    }
    return Array.from(this.tempFile).some(file => file.dataset_info);
  }

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * 如果是智能体会话，biz 为 ai_agent
   * 本接口返回的 biz 无意义
   */
  getSendParams(type) {
    if (this.tempFile) {
      if (!this.tempUploaded) {
        return {};
      }
      // tempUploaded 是一个数组
      if (this.tempUploaded.length <= 1) {
        // 图片
        const tempUploaded = this.tempUploaded[0]
        if (tempUploaded.ext && IMG_FILE_ICONS[tempUploaded.ext]) {
          let role_biz = 'chat_image';
          switch (type) {
            // 图片 - 解释
            case 'describe-image':
              role_biz = 'explain_image';
              break;
            // 图片 - 提取文字
            case 'ocr':
              role_biz = 'extract_image';
              break;
          }
          const fileInfo = HelperCore.copyObj(tempUploaded);
          fileInfo.url = minifyAliImage(fileInfo);
          fileInfo.file_type = FILE_TYPE.image;
          return {
            role_biz,
            file: [fileInfo],
          };
        }
        return {
          role_biz: 'doc',
          file: this.tempUploaded.map(item => {
            return {
              ...item,
              ...{
                file_type : getFileType(item),
              },
            }
          }),
        }
      } else {
        // 文件
        return {
          role_biz: 'doc',
          file: this.tempUploaded.map(item => {
            return {
              ...item,
              ...{
                file_type : getFileType(item),
              },
            }
          }),
        };
      }
    } else if (isEnableEnterpriseKbPage()) {
      // 企业新的知识库
      const output = kbStore.getOutput()
      return {
        dataset: {
          select_all: []
        },
        dataset_info: output,
      }
    }
    // 个人知识库
    const output = mutilKnowledgeStore.getOutput();
    if(output){
      const dataset = {};
      const dataset_info = {};
      const datasetInfo = output;
      Object.keys(datasetInfo).forEach(id => {
        const item = datasetInfo[id];
        const { selectAll, ...params } = item;
        if(selectAll){
          dataset.select_all = [];
        }
        else {
          dataset[id] = [];
        }
        dataset_info[id] = { ...params };
      });

      return {
        dataset_info,
        dataset,
      };
    }
  }

  onInputPaste = event => {
    if (event.clipboardData.files?.length) {
      userStore.doWhenLogin(() => {
        runInAction(() => {
          const file = event.clipboardData.files[0];
          const fileType = file.name.slice(file.name.lastIndexOf('.'));
          if (!ACCEPT_FILE_TYPE.includes(fileType.toLocaleLowerCase())) {
            HelpOther.toast(`不支持的文件类型`, 'info');
            return;
          }
          this.updateTempData('tempUploaded', null);
          this.updateTempData('tempFile', file);
        });
      });
    }
  };

  updateTempData(key, value) {
    console.info('>>> updateTempData', key, value)
    // 知识库与文件互斥
    try{
      if(key === 'tempFile'){
        if(Array.from(value).concat(Array.from(this.tempFile)).some(file => file.dataset_info)){
          this.reset();
        }
      }
    } catch(e){}

    runInAction(() => {
      try {
        if (!this[key]) this[key] = []
        // 分开处理
        if (key === 'tempFile') {
          if (this.tempFile.length < 1) {
            this.tempFile = cloneDeep(Array.from(value))
          } else {
            const tempFile = cloneDeep(Array.from(value))

            // 对比 this.tempFile 和 tempFile 两个数组，根据 name 属性判断是否重复，不重复的作新增到 this.tempFile
            this.tempFile = this.tempFile.concat(
              tempFile.filter(item =>
                !this.tempFile.some(file => file.name === item.name && item.name)
              )
            )
          }
        } else if (key === 'tempUploaded') {
          if (this.tempUploaded.length < 1) {
            this.tempUploaded = [value]
          } else {
            this.tempUploaded = this.tempUploaded.concat(value)
            // 以 this.tempFile 为准过滤 this.tempUploaded，判断条件：tempFile name 等于 tempUploaded originalName，并且 tempUploaded 有 originalName 的
            this.tempUploaded = this.tempUploaded.filter(item =>
              this.tempFile.some(file => file.name === item.originalName && item.originalName)
            )
            // 当删除操作到最后一个文件会有再次上传的行为，需要去重
            this.tempUploaded = uniqBy(this.tempUploaded, 'originalName')
          }
        }
      } catch (error) {
        console.error('updateTempData error', error);
      }
    });
  }

  handleInputFocus = () => {
    this.inputId = HelperCore.uniqueId();
  };

  handleInputFileCardClick = (type, prompt, detail) => {
    // debug
    console.log('handleInputFileCardClick', type, prompt, detail);
    const chatStore = chatStoreManager.current;
    if (chatStore.isAITyping) {
      return;
    }
    switch (type) {
      // 文档
      case 'summary':
      case 'generate-outline': {
        chatStore.sendMessage({
          input: prompt,
          role_biz: 'doc',
          file: [detail],
        });
        break;
      }
      // 图片 - 解释
      case 'describe-image': {
        const fileInfo = HelperCore.copyObj(detail);
        fileInfo.url = minifyAliImage(fileInfo);
        chatStore.sendMessage({
          input: prompt,
          role_biz: 'explain_image',
          file: [fileInfo],
        });
        break;
      }
      // 图片 - 提取文字
      case 'ocr': {
        const fileInfo = HelperCore.copyObj(detail);
        getImageMessageUrl(fileInfo).then(url => {
          fileInfo.url = url;
          chatStore.sendMessage({
            input: prompt,
            role_biz: 'extract_image',
            file: [fileInfo],
          });
        });
        break;
      }
    }
  };

  reset = () => {
    this.tempFile = null
    this.tempUploaded = null
  };

  close = (file) => {
    // debug
    HelperCore.log(`inputTypeStore close`, file);

    // 知识库
    if(file?.folder_id){
      try{
        const dataset_info = HelperCore.copyObj(this.tempFile[0].dataset_info);
        delete(dataset_info[file.folder_id]);
        this.tempFile = [{ dataset_info }];
      } catch(e){}
      return;
    }

    // 按 file name 属性移除 this.tempUploaded 和 this.tempFile 中的文件
    runInAction(() => {
      const name = file?.name
      if (name) {
        this.tempFile = this.tempFile.filter(item => item.name !== name)
        this.tempUploaded = this.tempUploaded.filter(item => item.originalName !== name)
      }
    })
  }
}

const inputTypeStore = new Store();
export default inputTypeStore;
